import React, { useEffect, useMemo, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import 'moment-timezone'
import _ from 'lodash'
import MaterialTable, { MTableBody, MTableHeader } from 'material-table'
import PassportVersionHistoryModal from '../../pages/project/PassportVersionHistoryModal'
import { tableIcons } from '../../helpers'

import {
	localization,
	styledHeaderSticky,
	arrayToLookup,
	modifyDefaultOptions,
	getStickyColumns,
	styledCellSticky,
} from '../../helpers/table.jsx'

import useAPI from '../../hooks/useAPI'
import useUserContext from '../../hooks/useUserContext'
import useToast from '../../hooks/useToast'
import ConfirmModal from '../common/ConfirmModal'
import ModalSendForRevision from '../common/ModalSendForRevision'
import ModalSendForRevisionWithDate from '../common/ModalSendForRevisionWithDate'
import StatusBadge from '../common/StatusBadge'
import TooltippedElement from '../common/TooltippedElement'
import {
	status_ru_m as status,
	status as status_eng,
} from '../../helpers/factdata'

import FilterMultiSelect from '../table/FilterMultiSelect'
import moment from 'moment'
import SlicedNameData from '../table/SlicedData'
import LoadingTable from '../table/LoadingTable'
import { FactDataActions } from '../common/Actions/FactDataActions'
import { BaseRequests } from '../../requests/BaseRequests'
import { mapMembersDataAfterPagination } from '../../helpers/Members/tableRegistry'
import useModalState from '../../hooks/useModalState'

const styledTable = {
	maxWidth: '100%',
}

export const TableRegistry = (props) => {
	const { setEditMemberID, filterStatus, tableRef, year, ...rest } = props
	const api = useAPI()
	const toast = useToast()
	const userCtx = useUserContext()
	const { projId, stratId, policyId, portfolioId } = useParams()
	const [members, setMembers] = useState([])
	const [deleteModalState, setDeleteModalValue] = useModalState()
	const [rejectModalState, setRejectModalValue] = useModalState()
	const [excludeModalState, setExcludeModalState] = useModalState()
	const [passportHistoryModal, setPassportHistoryModal] = useState(null)
	const [projsFilter, setProjsFilter] = useState([])
	const [isLoaded, setIsLoaded] = useState(false)

	useEffect(() => {
		const fetchData = async () => {
			await getData({
				callback: (result, currQuery) => {
					setMembers(mapMembersDataAfterPagination(result, currQuery).data)
					result?.approvedCount &&
						rest?.setApprovedMembersSp &&
						rest?.setApprovedMembersSp(result.approvedCount)
				},
			})
			const strategyProjs = await api.get(`/projs?year=${year}&${!!stratId ? `strategyId=${stratId}` : `policyId=${policyId}`}&latest=true`)
			setProjsFilter(strategyProjs.map(proj => ({...proj, nameProj: `${proj.fullNumber} ${proj.name}` })));
			setIsLoaded(true)
		}
		fetchData()
	}, [])

	const getData = async ({ query, callback }) => {
		const currQuery = query ?? tableRef.current?.state?.query ?? {}
		let url = BaseRequests.getUrlSearch(
			'/factdata/teammembers',
			getParamsForUrl(currQuery)
		)
		return await api.get(url).then((result) => callback(result, currQuery))
	}
	const updateQuery = () => {
		const query = tableRef?.current?.state?.query ?? {}
		const [currentYearQuery, currentStatusQuery] = [
			query?.year ?? year,
			query?.filterStatus ?? filterStatus,
		]
		return tableRef.current.onQueryChange({
			...tableRef.current.state.query,
			...{ year: currentYearQuery, filterStatus: currentStatusQuery },
		})
	}

	const handleSend = async (id) => {
		const response = await api.post(`/factdata/teammembers/${id}/send`)

		if (!response.error) {
			updateQuery()
		} else {
			toast.error(response?.error?.message || 'Не удалось произвести операцию')
		}
	}

	const handleReject = async (comment) => {
		const response = await api.post(
			`/factdata/teammembers/${rejectModalState.value}/reject`,
			{ comment }
		)
		if (!response.error) {
			updateQuery()
		} else
			toast.error(response?.error?.message || 'Не удалось произвести операцию')
	}

	const handleApprove = async (id) => {
		const response = await api.post(`/factdata/teammembers/${id}/approve`)
		if (!response.error) {
			updateQuery()
		} else
			toast.error(response?.error?.message || 'Не удалось произвести операцию')
	}

	const handleDelete = async () => {
		const result = await api.del(
			`/factdata/teammembers/${deleteModalState.value}`,
			{ id: deleteModalState.value }
		)
		if (result && !result.error) {
			toast.success('Участник команды успешно удален')
			updateQuery()
		}
		setDeleteModalValue(null)
	}

	const handleExclude = async (comment, exclusionDate) => {
		const currentMembers = await getCurrentMembers()

		const inc = moment(
			currentMembers?.data
				?.find((m) => m.id == excludeModalState.value)
				.inclusionDate.split('T')[0],
			'YYYY-MM-DD'
		)

		const exc = moment(exclusionDate, 'YYYY-MM-DD')
		if (exc.isBefore(inc)) {
			toast.error('Дата исключения не может быть раньше даты включения')
			return
		}

		const response = await api.post(
			`/factdata/teammembers/${excludeModalState.value}/exclude`,
			{ exclusionDate, comment }
		)

		if (!response.error) {
			setExcludeModalState(null)
			updateQuery()
		} else
			toast.error(response?.error?.message || 'Не удалось произвести операцию')
	}

	const actions = {
		edit: (id) => setEditMemberID(id),
		send: (id) => handleSend(id),
		approve: (id) => handleApprove(id),
		reject: (id) => setRejectModalValue(id),
		delete: (id) => setDeleteModalValue(id),
		exclude: (id) => setExcludeModalState(id),
	}

	const getCols = () => {
		const firstStickyCols = [
			{
				title: 'Действ',
				render: (rowData) => (
					<FactDataActions
						props={{ ...rowData, entityType: 'teamMember', style: { zIndex: 15 } }}
						actions={actions}
						userCtx={userCtx}
						stratId={stratId}
						portfId={portfolioId}
						policyId={policyId}
					/>
				),
				width: 80,
				cellStyle: styledCellSticky(),
				headerStyle: styledCellSticky(),
				filterCellStyle: styledCellSticky(),
			},
			{
				title: 'Статус',
				field: 'status',
				render: ({ status, comment }) =>
					comment ? (
						<TooltippedElement tooltip={comment}>
							<StatusBadge type={'teamMember'} status={status} />
						</TooltippedElement>
					) : (
						<StatusBadge type={'teamMember'} status={status} />
					),
				width: '170px',
			},
		]
		const baseCollsFirst = [
			...firstStickyCols,
			{
				title: '№ и название проекта',
				field: 'projId',
				cellStyle: { minWidth: '300px' },
				render: (rowData) => (
					<NavLink to={() => getProjLink(rowData)}>{rowData.nameProj}</NavLink>
				),
				lookup: arrayToLookup('nameProj', projsFilter, 'originId'),
				filterComponent: FilterMultiSelect,
				width: '300px',
			},
			{
				title: 'Руководитель проекта',
				field: 'projHeadId',
				width: 180,
				cellStyle: { minWidth: '180px' },
				render: (rowData) => <SlicedNameData dataToSlice={rowData.headName} />,
				lookup: arrayToLookup('headName', members, 'proj.head.userId'),
				filterComponent: FilterMultiSelect,
			},
			{
				title: 'ФИО участника',
				field: 'name',
				filtering: true,
				width: 200,
				cellStyle: { minWidth: '200px' },
				render: (rowData) => (
					<span
						onClick={() => setEditMemberID(rowData.id)}
						style={{ color: '#0D6EFD', cursor: 'pointer' }}>
						{rowData.fullName}
					</span>
				),
			},
		]

		const collsFirst = getStickyColumns(baseCollsFirst, 0)

		const restCols = [
			{
				title: <div style={{ width: '140px' }}>Роль</div>,
				field: 'role',
				width: 140,
				render: (rowData) => <span>{rowData.roleString}</span>,
				lookup: arrayToLookup('roleString', members, 'role'),
				filterComponent: FilterMultiSelect,
			},
			{
				title: 'Должность',
				field: 'post',
				width: 180,
				render: (rowData) => <span>{rowData.post}</span>,
				cellStyle: { minWidth: '180px' },
				lookup: arrayToLookup('post', members, 'post'),
				filterComponent: FilterMultiSelect,
			},
			{
				title: 'Вид трудоустройства',
				field: 'employmentType',
				width: 140,
				render: (rowData) => <span>{rowData.employmentType}</span>,
				cellStyle: { minWidth: '140px' },
				lookup: arrayToLookup('employmentType', members, 'employmentType'),
				filterComponent: FilterMultiSelect,
			},
			{
				title: 'Подразделение',
				field: 'division',
				width: 180,
				cellStyle: { minWidth: '220px' },
			},
			{
				title: 'Категория',
				field: 'category',
				width: 120,
				hidden: true,
				hiddenByColumnsButton: true,
				cellStyle: { minWidth: '120px' },
			},
			{
				title: 'Доля занимаемой ставки',
				field: 'rate',
				width: 120,
				hidden: true,
				hiddenByColumnsButton: true,
				cellStyle: { minWidth: '120px' },
			},
			{
				title: 'Дата включения в проект',
				field: 'inclusionDate',
				render: (rowData) => <span>{rowData.inclusionDateString}</span>,
				width: '180px',
				cellStyle: { minWidth: '180px' },
			},
			{
				title: 'Дата исключения из проекта',
				field: 'exclusionDate',
				render: (rowData) => <span>{rowData.exclusionDateString}</span>,
				width: '180px',
				cellStyle: { minWidth: '180px' },
			},
			{
				title: '% участия в проекте',
				field: 'share',
				render: (rowData) => <span>{rowData?.share * 100}%</span>,
				width: '135px',
				hidden: true,
				hiddenByColumnsButton: true,
				cellStyle: { minWidth: '135px' },
			},
		]
		return [...collsFirst, ...restCols]
	}

	const getProjLink = (row) => {
		return row.proj.strategyId
			? `/strats/${row.proj.strategyId}/projs/${row.proj.id}`
			: `/policies/${row.proj.policyId}/projs/${row.proj.id}`
	}

	const getParamsForUrl = (query) => {
		const filters = query?.filters?.reduce((accum, currentValue) => {
			return {
				...accum,
				...{
					[currentValue.column.field]: Array.isArray(currentValue.value)
						? currentValue.value.map((x) => x.value)
						: currentValue.value,
				},
			}
		}, {})

		return {
			year: query?.year ?? year,
			strategyId: stratId,
			policyId: policyId,
			portfId: portfolioId,
			projId: projId,
			status: query?.filterStatus ?? filterStatus,
			page: query.page,
			limit: 15,
			filter: JSON.stringify(filters),
		}
	}

	const getCurrentMembers = async (query) => {
		const currentMembers = await getData({
			query: query,
			callback: (result, query) => {
				result?.approvedCount != null &&
					rest.setApprovedMembersSp &&
					rest.setApprovedMembersSp(result.approvedCount)
				const mappedMembers = mapMembersDataAfterPagination(result, query)
				return mappedMembers
			},
		})

		return currentMembers
	}

	const table = useMemo(() => {
		return (
			<MaterialTable
				tableRef={tableRef}
				components={{
					Container: (props) => (
						<div className='special-filter-without-max' {...props} />
					),
					Body: (props) => (
						<>
							<MTableBody {...props} />
							<div id='portal-root'></div>
						</>
					),
					Header: (props) => (
						<MTableHeader {...props} tableHeadStyle={styledHeaderSticky} />
					),
				}}
				onFilterChange={() => {
					if (rest?.setApprovedMembersSp) {
						rest.setApprovedMembersSp(
							tableRef.current?.dataManager?.filteredData.filter(
								(x) => x.status === status_eng.approved
							).length
						)
					}
				}}
				title=''
				options={modifyDefaultOptions({
					columnsButton: true,
					pageSize: 15,
					pageSizeOptions: [15, 30, 50],
					headerStyle: {
						fontSize: 12,
						lineHeight: 1.3,
						padding: '8px 16px',
						zIndex: 0,
					},
					filterCellStyle: {
						padding: '8px 16px',
						position: 'sticky',
						top: 53,
						background: 'white',
					},
					filtering: true,
					sorting: true,
					search: false,
					paging: true,
					maxBodyHeight: 'calc(100vh - 476px)',
				})}
				icons={tableIcons}
				localization={localization}
				columns={getCols()}
				data={getCurrentMembers}
			/>
		)
	}, [members, projsFilter])

	return (
		<div className='tab-subproject-register' style={{ color: 'initial' }}>
			<LoadingTable
				spinnerStyle={{ marginTop: '80px' }}
				loadingObject={{ isLoaded }}>
				<div style={styledTable}>{table}</div>
			</LoadingTable>
			{passportHistoryModal && (
				<PassportVersionHistoryModal
					pass={passportHistoryModal}
					onClose={() => setPassportHistoryModal(null)}
				/>
			)}
			<ModalSendForRevision
				show={rejectModalState.show}
				onClose={() => setRejectModalValue(null)}
				onSubmit={(comment) => handleReject(comment)}
				headerText='Не согласовать'
				label='Комментарий'
			/>
			{excludeModalState.show && (
				<ModalSendForRevisionWithDate
					show={excludeModalState.show}
					onClose={() => setExcludeModalState(null)}
					onSubmit={(comment, date) => handleExclude(comment, date)}
					headerText='Исключение участника'
					label='Комментарий'
					labelDate='Дата исключения'
				/>
			)}
			<ConfirmModal
				show={deleteModalState.show}
				onClose={() => setDeleteModalValue(null)}
				onConfirm={() => handleDelete()}
				headerText='Удаление участника команды'
				message='Участник команды будет удален, данное действие нельзя отменить. Удалить участника команды?'
			/>
		</div>
	)
}

export default TableRegistry
